import type { ReactNode } from "react";
import type {
  BillingViewModel,
  SiteExportViewModel,
  TicketListViewModel,
  UsageViewModel,
} from "@lumora/core";
import { BillingSection } from "./BillingSection.js";
import { OwnerApiPlayground } from "./OwnerApiPlayground.js";
import { SiteExportSection } from "./SiteExportSection.js";
import { TicketsSection } from "./TicketsSection.js";
import { UsageSection } from "./UsageSection.js";
import { LogsSection, type LogsSectionProps } from "./LogsSection.js";
import { PayoutsSection, type PayoutsSectionProps } from "./PayoutsSection.js";
import { EdgeDevicePairSection } from "./EdgeDevicePairSection.js";

export interface DashboardPageProps {
  ticketsVM: TicketListViewModel;
  usageVM: UsageViewModel;
  billingVM: BillingViewModel;
  siteExportVM: SiteExportViewModel;
  /** Remembered tenant slug for copy — never trust for auth. */
  siteSlug?: string | undefined;
  /** Deep link from Pi claim slip QR (?pair=). */
  pairCode?: string | undefined;
  onStartHosting?: (() => void) | undefined;
  /** Pass for users with logs:read (owners); omit to hide the Logs section. */
  logsClient?: LogsSectionProps["client"] | undefined;
  /** Pass (with siteSlug) for owners/managers to show Stripe Connect payout setup. */
  payoutsClient?: PayoutsSectionProps["client"] | undefined;
  /** Sign-in security block (MFA banner + panel), rendered first. */
  securitySlot?: ReactNode;
  className?: string;
}

/** DashboardPage composes the portal sections. Pure layout — zero logic. */
export function DashboardPage({
  ticketsVM,
  usageVM,
  billingVM,
  siteExportVM,
  siteSlug,
  pairCode,
  onStartHosting,
  logsClient,
  payoutsClient,
  securitySlot,
  className,
}: DashboardPageProps) {
  return (
    <div className={className} data-lumora-view="dashboard">
      {siteSlug ? (
        <p className="portal-tenant" data-testid="portal-tenant">
          Managing <strong>{siteSlug}</strong>
        </p>
      ) : null}
      {securitySlot}
      <EdgeDevicePairSection siteSlug={siteSlug} initialCode={pairCode} />
      <OwnerApiPlayground />
      <BillingSection viewModel={billingVM} onStartHosting={onStartHosting} />
      <SiteExportSection viewModel={siteExportVM} />
      <TicketsSection viewModel={ticketsVM} />
      <UsageSection viewModel={usageVM} />
      {payoutsClient && siteSlug ? (
        <PayoutsSection siteSlug={siteSlug} client={payoutsClient} />
      ) : null}
      {logsClient ? <LogsSection client={logsClient} /> : null}
    </div>
  );
}
